import React, { useState, useEffect } from "react";
import MyModal from "../../Common/my-modal/my-modal.component";
import CrossIcon from "../../icons/cross.icon";
import { CountryDropdown } from "./country-dropdown/country-dropdown";
import { CurrencyDropdown } from "./currency-dropdown/currency-dropdown";
import { PlanDropdown } from "./plan-dropdown/plan-dropdown";
import styles from "./add-distributor-modal.module.scss";

const initialFormData = {
  companyName: "",
  principalContact: "",
  accountEmail: "",
  country: "",
  currency: "",
  distributionRegions: [],
  plan: "",
};

export const AddDistributorModal = ({ show, onHide, onContinue }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [regionInput, setRegionInput] = useState("");

  useEffect(() => {
    if (show) {
      // Reset form when modal opens
      setFormData(initialFormData);
      setRegionInput("");
    }
  }, [show]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRemoveRegion = (regionToRemove) => {
    setFormData((prev) => ({
      ...prev,
      distributionRegions: prev.distributionRegions.filter(
        (region) => region !== regionToRemove,
      ),
    }));
  };

  const handleRegionInputKeyDown = (e) => {
    if (e.key === "Enter" && regionInput.trim()) {
      e.preventDefault();
      const trimmedValue = regionInput.trim();
      if (!formData.distributionRegions.includes(trimmedValue)) {
        setFormData((prev) => ({
          ...prev,
          distributionRegions: [...prev.distributionRegions, trimmedValue],
        }));
      }
      setRegionInput("");
    }
  };

  const handleCancel = () => {
    if (onHide) {
      onHide();
    }
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue(formData);
    }
    if (onHide) {
      onHide();
    }
  };

  return (
    <MyModal show={show} onHide={onHide} dialogClassName="add-distributor-modal-dialog">
      <MyModal.Header className={styles.header} closeButton onCrossClick={onHide}>
        <MyModal.HeaderTitle className={styles.headerTitle}>
          Add Distributor
        </MyModal.HeaderTitle>
      </MyModal.Header>

      <MyModal.Body className={styles.body}>
        <p className={styles.description}>
          Enter user details below. We&apos;ll email an activation link to the
          company<br />address — this will grant them access to HighGround.
        </p>

        <div className={styles.formGroup}>
          <label htmlFor="companyName" className={styles.label}>
            Company Name*
          </label>
          <div className={styles.inputWrapper}>
            <input
              id="companyName"
              type="text"
              value={formData.companyName}
              onChange={(e) => handleInputChange("companyName", e.target.value)}
              className={styles.input}
              required
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="principalContact" className={styles.label}>
            Principal Contact*
          </label>
          <div className={styles.inputWrapper}>
            <input
              id="principalContact"
              type="text"
              value={formData.principalContact}
              onChange={(e) =>
                handleInputChange("principalContact", e.target.value)
              }
              className={styles.input}
              required
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="accountEmail" className={styles.label}>
            Account Email*
          </label>
          <div className={styles.inputWrapper}>
            <input
              id="accountEmail"
              type="email"
              value={formData.accountEmail}
              onChange={(e) =>
                handleInputChange("accountEmail", e.target.value)
              }
              className={styles.input}
              required
            />
          </div>
        </div>

        <div className={styles.rowGroup}>
          <div className={styles.formGroup}>
            <label htmlFor="country" className={styles.label}>
              Country
            </label>
            <CountryDropdown
              value={formData.country}
              onChange={(value) => handleInputChange("country", value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="currency" className={styles.label}>
              Currency
            </label>
            <CurrencyDropdown
              value={formData.currency}
              onChange={(value) => handleInputChange("currency", value)}
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="distributionRegions" className={styles.label}>
            Distribution Regions
          </label>
          <div className={styles.chipsWrapper}>
            <div className={styles.chipsContainer}>
              {formData.distributionRegions.map((region) => (
                <div key={region} className={styles.chip}>
                  <span className={styles.chipText}>{region}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveRegion(region)}
                    className={styles.chipRemoveButton}
                    aria-label={`Remove ${region}`}
                  >
                    <CrossIcon width="8px" height="8px" />
                  </button>
                </div>
              ))}
              <input
                id="distributionRegions"
                type="text"
                className={styles.regionInput}
                value={regionInput}
                onChange={(e) => setRegionInput(e.target.value)}
                onKeyDown={handleRegionInputKeyDown}
                placeholder={formData.distributionRegions.length === 0 ? "Enter region and press Enter" : ""}
              />
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.formGroup}>
          <label htmlFor="plan" className={styles.label}>
            Plan
          </label>
          <PlanDropdown
            value={formData.plan}
            onChange={(value) => handleInputChange("plan", value)}
          />
        </div>
      </MyModal.Body>

      <MyModal.Footer className={styles.footer}>
        <MyModal.CancelButton onClick={handleCancel} className={styles.cancelButton}>
          Cancel
        </MyModal.CancelButton>
        <MyModal.ApplyButton onClick={handleContinue} className={styles.continueButton}>
          Continue
        </MyModal.ApplyButton>
      </MyModal.Footer>
    </MyModal>
  );
};
